import React, { useState, useEffect } from "react";
import { 
  Card, 
  Row, 
  Col, 
  Select, 
  Table, 
  Input, 
  Button, 
  message, 
  Space, 
  Typography,
  Divider,
  Spin
} from "antd";
import { 
  SearchOutlined, 
  ReloadOutlined, 
  SaveOutlined,
  PrinterOutlined,
  FileTextOutlined
} from "@ant-design/icons";
import API from "../../api";

const { Title, Text } = Typography;
const { Option } = Select;

export default function StockAudit() {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [taxes, setTaxes] = useState([]);

  // Fetch stores on component mount
  useEffect(() => {
    fetchStores();
  }, []);

  // Fetch stock audit data when store is selected
  useEffect(() => {
    if (selectedStore) {
      fetchStockAuditData();
      fetchCategories();
      fetchBrands();
      fetchTaxes();
    }
  }, [selectedStore]);

  const fetchStores = async () => {
    try {
      const response = await API.get("/stores");
      setStores(response.data);
    } catch (error) {
      console.error("Error fetching stores:", error);
      message.error("Failed to load stores");
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await API.get("/category");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await API.get("/brands");
      setBrands(response.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  const fetchTaxes = async () => {
    try {
      const response = await API.get("/stock-audit/taxes");
      setTaxes(response.data);
    } catch (error) {
      console.error("Error fetching taxes:", error);
    }
  };

  const fetchStockAuditData = async () => {
    if (!selectedStore) return;
    
    setLoading(true);
    try {
      const params = {
        storeId: selectedStore,
        ...(selectedCategory && { categoryId: selectedCategory }),
        ...(selectedBrand && { brandId: selectedBrand }),
        ...(searchQuery && { search: searchQuery })
      };

      const response = await API.get("/stock-audit", { params });
      setStockData(response.data);
    } catch (error) {
      console.error("Error fetching stock audit data:", error);
      message.error("Failed to load stock audit data");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (record, field, value) => {
    setStockData(prevData => 
      prevData.map(item => 
        item._id === record._id 
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  const handleSaveChanges = () => {
    const updatedItems = stockData.filter(item => item.hasChanges);
    
    if (updatedItems.length === 0) {
      message.info("No changes to save");
      return;
    }

    message.info(`Save functionality will be implemented later. ${updatedItems.length} items have changes.`);
  };

  const handleIndividualUpdate = (record) => {
    message.info(`Individual update functionality will be implemented later for product: ${record.productName || record.name}`);
  };

  const handlePrintReport = () => {
    // TODO: Implement print functionality
    message.info("Print functionality will be implemented");
  };

  const handleExportReport = () => {
    // TODO: Implement export functionality
    message.info("Export functionality will be implemented");
  };

  const columns = [
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
      width: 200,
      fixed: "left",
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: "bold" }}>{text}</div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            SKU: {record.sku || "Not Set"}
          </div>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      key: "categoryName",
      width: 120,
      render: (text) => text || "N/A",
    },
    {
      title: "Brand",
      dataIndex: "brandName",
      key: "brandName",
      width: 120,
      render: (text) => text || "N/A",
    },
    {
      title: "Current Stock",
      dataIndex: "currentQuantity",
      key: "currentQuantity",
      width: 120,
      align: "center",
      render: (text) => (
        <span style={{ 
          fontWeight: "bold",
          color: text > 0 ? "#52c41a" : "#ff4d4f"
        }}>
          {text || 0}
        </span>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 120,
      align: "center",
      render: (text, record) => (
        <Input
          type="number"
          value={text}
          onChange={(e) => {
            const value = parseFloat(e.target.value) || 0;
            handleFieldChange(record, "quantity", value);
            handleFieldChange(record, "hasChanges", true);
          }}
          style={{ textAlign: "center" }}
          min={0}
        />
      ),
    },
    {
      title: "Tracking Number",
      dataIndex: "trackingNumber",
      key: "trackingNumber",
      width: 150,
      render: (text, record) => (
        <Input
          value={text || ""}
          onChange={(e) => {
            handleFieldChange(record, "trackingNumber", e.target.value);
            handleFieldChange(record, "hasChanges", true);
          }}
          placeholder="Enter tracking number"
        />
      ),
    },
    {
      title: "MRP",
      dataIndex: "mrp",
      key: "mrp",
      width: 120,
      align: "right",
      render: (text, record) => (
        <Input
          type="number"
          value={text}
          onChange={(e) => {
            const value = parseFloat(e.target.value) || 0;
            handleFieldChange(record, "mrp", value);
            handleFieldChange(record, "hasChanges", true);
          }}
          style={{ textAlign: "right" }}
          min={0}
          step={0.01}
        />
      ),
    },
    {
      title: "Selling Price",
      dataIndex: "sellingPrice",
      key: "sellingPrice",
      width: 130,
      align: "right",
      render: (text, record) => (
        <Input
          type="number"
          value={text}
          onChange={(e) => {
            const value = parseFloat(e.target.value) || 0;
            handleFieldChange(record, "sellingPrice", value);
            handleFieldChange(record, "hasChanges", true);
          }}
          style={{ textAlign: "right" }}
          min={0}
          step={0.01}
        />
      ),
    },
    {
      title: "Purchase Price",
      dataIndex: "purchasePrice",
      key: "purchasePrice",
      width: 130,
      align: "right",
      render: (text, record) => (
        <Input
          type="number"
          value={text}
          onChange={(e) => {
            const value = parseFloat(e.target.value) || 0;
            handleFieldChange(record, "purchasePrice", value);
            handleFieldChange(record, "hasChanges", true);
          }}
          style={{ textAlign: "right" }}
          min={0}
          step={0.01}
        />
      ),
    },
    {
      title: "GST %",
      dataIndex: "gst",
      key: "gst",
      width: 120,
      align: "center",
      render: (text, record) => (
        <Select
          style={{ width: "100%" }}
          value={record.gst}
          onChange={(value) => {
            handleFieldChange(record, "gst", value);
            handleFieldChange(record, "hasChanges", true);
          }}
          placeholder="Select GST"
          size="small"
        >
          {taxes.map((tax) => (
            <Option key={tax._id} value={tax._id}>
              {tax.name} ({tax.percentage}%)
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Total Value",
      key: "totalValue",
      width: 120,
      align: "right",
      render: (record) => {
        const total = (record.quantity || 0) * (record.purchasePrice || 0);
        return (
          <span style={{ fontWeight: "bold", color: "#1890ff" }}>
            ₹{total.toFixed(2)}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      align: "center",
      fixed: "right",
      render: (record) => (
        <Button
          type="primary"
          size="small"
          icon={<SaveOutlined />}
          onClick={() => handleIndividualUpdate(record)}
          disabled={!record.hasChanges}
          style={{
            backgroundColor: record.hasChanges ? "#52c41a" : "#d9d9d9",
            borderColor: record.hasChanges ? "#52c41a" : "#d9d9d9"
          }}
        >
          Update
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Title level={4} style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: "8px" }} />
              Stock Audit
            </Title>
          </Col>
          <Col xs={24} sm={12} md={16}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchStockAuditData}
                loading={loading}
                disabled={!selectedStore}
              >
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveChanges}
                loading={loading}
                disabled={!selectedStore}
              >
                Save Changes
              </Button>
              <Button
                icon={<PrinterOutlined />}
                onClick={handlePrintReport}
                disabled={!selectedStore}
              >
                Print
              </Button>
            </Space>
          </Col>
        </Row>

        <Divider />

        {/* Filters Section */}
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong>Select Store (for Stock Operations) *</Text>
              <Select
                style={{ width: "100%", marginTop: "4px" }}
                placeholder="Select a store for stock operations"
                value={selectedStore}
                onChange={(value) => {
                  setSelectedStore(value);
                  setSearchQuery("");
                  setSelectedCategory(null);
                  setSelectedBrand(null);
                }}
                showSearch
                optionFilterProp="children"
              >
                {stores.map((store) => (
                  <Option key={store._id} value={store._id}>
                    {store.name}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong>Category</Text>
              <Select
                style={{ width: "100%", marginTop: "4px" }}
                placeholder="All Categories"
                value={selectedCategory}
                onChange={(value) => {
                  setSelectedCategory(value);
                  fetchStockAuditData();
                }}
                allowClear
              >
                {categories.map((category) => (
                  <Option key={category._id} value={category._id}>
                    {category.name}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong>Brand</Text>
              <Select
                style={{ width: "100%", marginTop: "4px" }}
                placeholder="All Brands"
                value={selectedBrand}
                onChange={(value) => {
                  setSelectedBrand(value);
                  fetchStockAuditData();
                }}
                allowClear
              >
                {brands.map((brand) => (
                  <Option key={brand._id} value={brand._id}>
                    {brand.name}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong>Search Product</Text>
              <Input
                style={{ marginTop: "4px" }}
                placeholder="Search by product name or SKU"
                prefix={<SearchOutlined />}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  // Debounce search
                  setTimeout(() => {
                    if (e.target.value === searchQuery) {
                      fetchStockAuditData();
                    }
                  }, 500);
                }}
                allowClear
              />
            </div>
          </Col>
        </Row>

        {/* Stock Data Table */}
        {selectedStore ? (
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={stockData}
              rowKey="_id"
              scroll={{ x: 1500 }}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              size="small"
              bordered
            />
          </Spin>
        ) : (
          <div style={{ 
            textAlign: "center", 
            padding: "60px 20px",
            color: "#999"
          }}>
            <FileTextOutlined style={{ fontSize: "48px", marginBottom: "16px" }} />
            <div>Please select a store to view stock audit data</div>
            <div style={{ fontSize: "12px", marginTop: "8px" }}>
              Store selection is used for stock operations only
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
